import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { jwtDecode } from "jwt-decode";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import Icon from "components/Icon";
import BarraInferior from "components/BarraInferior";
import MenuBlocos from "components/MenuBlocos";
import DialogoConfirmacao from "components/DialogoConfirmacao";
import AssinaturaMarca from "components/AssinaturaMarca";
import InitialsAvatar from "components/InitialsAvatar";
import MensagemErro from "components/MensagemErro";
import apiClient from 'services/api';
import { abrirOverlay } from 'services/overlays';
import { lerToken } from 'services/sessao';
import { ehNativo } from 'services/plataforma';
import { encerrarSessao } from 'services/autenticacao';
import { EVENTO_PUSH_RECEBIDO } from 'services/pushNotificacoes';
import { rotaDaNotificacao } from 'services/destinoDaNotificacao';
import { descreverErro } from 'services/erros';
import { usePendenciaAssinatura } from 'hooks/usePendenciaAssinatura';
import { usePendenciaVerificacao } from 'hooks/usePendenciaVerificacao';
import { MODO, useModoConta } from 'hooks/useModoConta';
import { useAvatarUsuario } from 'hooks/useAvatarUsuario';
import { useIsAdmin } from 'hooks/useIsAdmin';
import { linksInstitucionais, formularioContato } from 'config/linksInstitucionais';

// Tem que acompanhar a duração de `notificacao-achatar` em index.css: a linha só
// sai da lista depois que a animação de achatamento termina.
const DURACAO_SAIDA_NOTIFICACAO = 320;

// Esconde de leitores de tela e do tab order tudo em document.body que não
// esteja dentro de um dos `targets` (nem seja ancestral deles). Usado pra
// deixar só o header + o painel do menu mobile "vivos" enquanto o resto da
// página fica coberto — diferente de esconder só o pai comum dos dois, que
// esconderia ele mesmo e quebraria o botão que fecha o menu.
function hideBackgroundExcept(targets) {
	const validTargets = targets.filter(Boolean);
	if (validTargets.length === 0) return () => {};

	const targetsSet = new Set(validTargets);
	const ancestorsSet = new Set();
	validTargets.forEach((target) => {
		let node = target.parentElement;
		while (node && node !== document.body) {
			ancestorsSet.add(node);
			node = node.parentElement;
		}
	});

	const hidden = [];
	const walk = (parent) => {
		Array.from(parent.children).forEach((child) => {
			if (targetsSet.has(child)) return; // é um alvo: não mexe em nada dentro dele
			if (ancestorsSet.has(child)) {
				walk(child); // está no caminho até um alvo: entra, mas não esconde ele mesmo
				return;
			}
			hidden.push({
				el: child,
				ariaHidden: child.getAttribute("aria-hidden"),
				hadInert: child.hasAttribute("inert"),
			});
			child.setAttribute("aria-hidden", "true");
			child.setAttribute("inert", "");
		});
	};
	walk(document.body);

	return () => {
		hidden.forEach(({ el, ariaHidden, hadInert }) => {
			if (ariaHidden === null) el.removeAttribute("aria-hidden");
			else el.setAttribute("aria-hidden", ariaHidden);
			if (!hadInert) el.removeAttribute("inert");
		});
	};
}

/**
 * @param {boolean} abas  false nas telas que já têm uma barra fixa no rodapé (/agendar): no app o
 *                        header continua compacto, mas a barra de abas não é montada — empilhar as
 *                        duas comeria ~170px da tela mais longa do app.
 */
export default function NavBar({ abas = true }) {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isScrolled, setIsScrolled] = useState(false);

	// Refs para gerenciamento de foco e a11y do menu mobile (painel abaixo do header).
	// hamburgerBtnRef é "o botão que abre o menu": o hambúrguer na web, a aba do avatar no app —
	// é para ele que o foco volta quando o painel fecha, seja qual for.
	const navRef = useRef(null);
	const hamburgerBtnRef = useRef(null);
	const menuPanelRef = useRef(null);
	const mobileMenuPortalRef = useRef(null);
	const barraInferiorRef = useRef(null);
	const ajudaConteudoRef = useRef(null);

	// A gaveta "Ajuda" já abre quando a pessoa está numa das páginas que ela lista: o menu
	// confirma onde ela está em vez de esconder isso atrás de um toque. Cada página monta
	// seu próprio <NavBar/> (ver os imports em pages/*), então o valor inicial basta — não
	// há como o pathname mudar sem remontar o componente.
	//
	// `soNaWeb` sai no app (ver o "Excluir minha conta" no config).
	const linksAjuda = linksInstitucionais.filter((link) => !(link.soNaWeb && ehNativo()));
	// O prefixo cobre os artigos da Central de ajuda (/ajuda/<slug>), que não estão na lista.
	const estaEmPaginaAjuda = linksAjuda.some((link) => link.to === pathname)
		|| pathname.startsWith('/ajuda/');
	const [ajudaAberta, setAjudaAberta] = useState(estaEmPaginaAjuda);

	// Com sessão (web ou app) a Ajuda não é gaveta: é um sheet aberto por cima do menu em blocos
	// (components/MenuBlocos.jsx). Começa sempre fechado — abrir sozinho, como a gaveta faz, cobriria
	// o menu inteiro no toque que só queria abri-lo. A gaveta acima fica para o visitante.
	const [ajudaEmSheetAberta, setAjudaEmSheetAberta] = useState(false);

	// A pergunta antes de encerrar a sessão. Um estado só para os dois "Sair" — o do menu
	// sanduíche e o do dropdown do avatar —, porque o diálogo é um só, portado pro body.
	const [confirmarSaida, setConfirmarSaida] = useState(false);

	// O fechamento do painel, para a pilha de overlays: é o que o botão voltar do Android chama
	// quando o menu está aberto. Em ref, e não a função direta, porque o efeito que registra na
	// pilha depende só de `isMenuOpen` — passar a função obrigaria a incluí-la nas dependências e o
	// painel se reinscreveria a cada render.
	const fecharMenuRef = useRef(() => setIsMenuOpen(false));

	let firstName = '';
	// O nome inteiro, para o InitialsAvatar: é dele que saem as iniciais e a cor, as mesmas que os
	// outros veem quando olham para esta pessoa.
	let userName = '';

	const [showNotifications, setShowNotifications] = useState(false);
	const [showAccountMenu, setShowAccountMenu] = useState(false);
	const [notifications, setNotifications] = useState([]);
	const [loadingNotifications, setLoadingNotifications] = useState(false);
	const [notifError, setNotifError] = useState(null);
	const [deletingId, setDeletingId] = useState(null);
	const [removingId, setRemovingId] = useState(null);
	const removalTimeoutRef = useRef(null);
	const [unreadCount, setUnreadCount] = useState(0);

	useEffect(() => {
		const onScroll = () => setIsScrolled(window.scrollY > 4);
		onScroll();
		window.addEventListener("scroll", onScroll, { passive: true });
		return () => window.removeEventListener("scroll", onScroll);
	}, []);

	// Painel fechado: mesmo fora da tela/invisível, os links lá dentro continuam
	// no DOM — sem isto, dá pra "tabular" até eles mesmo escondidos.
	//
	// Painel aberto: trava o scroll da página, tira o `inert` do painel (tem
	// que vir ANTES do focus() abaixo — um elemento inert não é focável, então
	// fazer isso em dois efeitos separados criava uma corrida: se o efeito que
	// focava rodasse primeiro, o painel ainda estava inert e o foco não saía
	// do botão) e move o foco pra dentro do painel (padrão WAI-ARIA de dialog —
	// como não há mais um X dedicado lá dentro, o foco vai pro próprio painel).
	// Ao fechar — por Escape, num link, ou no botão do header (hambúrguer vira
	// X) — o foco volta pro botão, em vez de "sumir" e deixar quem navega por
	// teclado perdido na página.
	useEffect(() => {
		const panel = menuPanelRef.current;
		if (!isMenuOpen) {
			if (panel) {
				panel.inert = true;
				panel.setAttribute("aria-hidden", "true");
			}
			return;
		}
		// A pilha do services/overlays, e não o `document.body.style.overflow = "hidden"` que estava
		// aqui. Aquele era o segundo mecanismo de trava do projeto: inline (vencia a classe do outro
		// por especificidade), sem contagem e sem media query — travava até no desktop, onde este
		// painel nem existe (md:hidden). Agora os quatro overlays contam no mesmo lugar, e é dele
		// que sai também a resposta ao botão voltar do Android.
		const removerDaPilha = abrirOverlay(fecharMenuRef);
		if (panel) {
			panel.inert = false;
			panel.removeAttribute("aria-hidden");
			panel.focus();
		}
		const hamburgerBtn = hamburgerBtnRef.current;
		return () => {
			removerDaPilha();
			hamburgerBtn?.focus();
		};
	}, [isMenuOpen]);

	// Escape fecha as notificações — a gaveta do desktop é modal e cobre o sino que a abriu.
	useEffect(() => {
		if (!showNotifications) return;
		const onKeyDown = (e) => {
			if (e.key === "Escape") setShowNotifications(false);
		};
		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, [showNotifications]);

	// Escape do painel, num efeito à parte do de foco/rolagem acima: ele precisa reagir
	// também à confirmação de saída, e o de cima não pode rodar de novo por causa dela —
	// a limpeza devolve o foco ao hambúrguer e o corpo refoca o painel, roubando o foco do
	// diálogo que acabou de abrir.
	//
	// Com a confirmação aberta o painel simplesmente não escuta: os dois listeners vivem no
	// document, e um Escape fecharia o painel junto com o diálogo — desmontando o fundo
	// inteiro justamente para quem desistiu de sair. O sheet da Ajuda do menu em blocos é o mesmo caso.
	useEffect(() => {
		if (!isMenuOpen || confirmarSaida || ajudaEmSheetAberta) return;
		const onKeyDown = (e) => {
			if (e.key === "Escape") setIsMenuOpen(false);
		};
		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, [isMenuOpen, confirmarSaida, ajudaEmSheetAberta]);

	// O menu fecha por vários caminhos (aba, link, troca de modo, voltar do Android); o sheet da
	// Ajuda não pode sobrar aberto em cima de um menu que já saiu.
	useEffect(() => {
		if (!isMenuOpen) setAjudaEmSheetAberta(false);
	}, [isMenuOpen]);

	// Painel aberto: esconde o restante da página de leitores de tela e do tab
	// order. O header continua "vivo" — é nele que mora o botão que fecha o
	// menu (hambúrguer vira X) — só o conteúdo por trás do painel é escondido.
	//
	// A barra de abas entra na lista: com o menu aberto ela continua na tela, e é a aba do avatar
	// que o fecha — inert, ela viraria um pedaço de interface desenhado mas morto.
	useEffect(() => {
		if (!isMenuOpen) return;
		return hideBackgroundExcept([navRef.current, mobileMenuPortalRef.current, barraInferiorRef.current]);
	}, [isMenuOpen]);

	// Gaveta fechada: o conteúdo continua no DOM — é o que permite animar a altura —, então
	// sem isto dá pra "tabular" até os seis links escondidos. Mesmo tratamento que o painel
	// do menu recebe acima; via ref porque o React 18 ainda não aceita `inert` como prop.
	useEffect(() => {
		const conteudo = ajudaConteudoRef.current;
		if (!conteudo) return;
		conteudo.inert = !ajudaAberta;
		if (ajudaAberta) conteudo.removeAttribute("aria-hidden");
		else conteudo.setAttribute("aria-hidden", "true");
	}, [ajudaAberta]);

	useEffect(() => () => clearTimeout(removalTimeoutRef.current), []);

	// Função para excluir notificação
	const handleDeleteNotification = async (notifId) => {
		if (!notifId) return;
		setDeletingId(notifId);
		setNotifError(null);
		try {
			await apiClient.delete(`/notificacao/delete/${notifId}`);
			// A linha fica no DOM até a animação de achatamento terminar — tirar do
			// estado na hora faria o item sumir de um quadro pro outro, sem o usuário
			// perceber qual saiu. `deletingId` também só é limpo aqui, senão o ícone
			// voltaria a ser a lixeira no meio da saída da linha.
			setRemovingId(notifId);
			removalTimeoutRef.current = setTimeout(() => {
				setNotifications((prev) => prev.filter((n) => n.id !== notifId));
				setRemovingId(null);
				setDeletingId(null);
			}, DURACAO_SAIDA_NOTIFICACAO);
		} catch (err) {
			setNotifError(descreverErro('NTF-201', err));
			setDeletingId(null);
		}
	};
	let userId = null;
	const token = lerToken();
	if (token) {
		try {
			const decodedToken = jwtDecode(token);
			userName = decodedToken.userName || '';
			if (userName) {
				firstName = userName.split(' ')[0];
			}
			userId = decodedToken.userId;
		} catch (error) {
			console.error("Erro ao decodificar o token: ", error);
		}
	}
	// A casca do app logado: header compacto (56px, sem hambúrguer, logo à esquerda) e a barra de
	// abas no rodapé (components/BarraInferior.jsx). Só no app nativo e só com sessão — o visitante
	// que chega aos Termos a partir do login continua com o header de hoje, porque as abas levam a
	// telas privadas. Na web `ehNativo()` é sempre false, e nada disto existe.
	//
	// As duas classes no body são o que faz o CSS acompanhar (styles/index.css): --altura-header
	// encolhe para os painéis que se posicionam a partir da base do header, e --altura-barra-inferior
	// reserva o rodapé. Ligadas aqui, e não no boot, porque só a NavBar sabe se está montada nesta
	// tela — Login e o funil do profissional têm cabeçalho próprio e não podem ganhar o padding.
	const modoApp = ehNativo() && !!token;
	const comAbas = modoApp && abas;

	useEffect(() => {
		if (!modoApp) return;
		document.body.classList.add("app-header-compacto");
		if (comAbas) document.body.classList.add("app-com-abas");
		return () => {
			document.body.classList.remove("app-header-compacto", "app-com-abas");
		};
	}, [modoApp, comAbas]);

	// Quem decide é o backend (app.admin.usuario-ids, via GET /admin/me). Antes era
	// "Number(userId) === 1" aqui: o id do administrador existia em dois lugares que podiam
	// discordar em silêncio — o menu aparecendo para quem tomava 403 ao usá-lo, ou sumindo para
	// quem tinha acesso de verdade. O hook devolve null enquanto não sabe, e null esconde o menu.
	//
	// No app nem se pergunta: o build nativo sai sem o administrativo (REACT_APP_COM_ADMIN, em
	// App.jsx), então não há link a mostrar, e o GET por navegação seria à toa.
	const isAdmin = useIsAdmin(modoApp ? null : userId) === true;

	// Cliente não tem assinatura nem documento a enviar, então as duas rotas voltam vazias e o aviso
	// nunca aparece — não é preciso descobrir o perfil aqui só para decidir se sinaliza.
	//
	// Um sinal só para as duas pendências: o ícone significa "seu cadastro exige atenção", e quem o
	// vê não precisa saber de antemão qual das duas coisas falta. Quem separa é o destino.
	//
	// Nenhum dos dois hooks tem cache, então isso soma um GET por navegação (o NavBar remonta a cada
	// troca de página). É deliberado: cachear, como faz o usePerfilProfissional, deixaria o ícone
	// aceso depois de a pessoa resolver a pendência — justamente quando ele precisa apagar.
	const assinaturaPendente = usePendenciaAssinatura(!!token);
	const { pendente: documentoPendente } = usePendenciaVerificacao(!!token);
	const cadastroPendente = assinaturaPendente || documentoPendente;

	// O documento vem primeiro: sem ele o perfil não aparece nem com a mensalidade em dia.
	const destinoCadastro = documentoPendente
		? "/usuario/editar?secao=conta"
		: assinaturaPendente
			? "/usuario/editar?secao=assinatura"
			: "/usuario/editar";

	// Define se o item de destaque do menu é "Buscar profissional" (quem está contratando) ou "Fale
	// conosco" (a profissional no modo trabalhando, e o visitante deslogado, que ainda não é
	// cliente). Segue o MODO, e não o papel cru: a profissional também contrata, e no modo
	// contratando a busca é o item principal dela como é do cliente (ver hooks/useModoConta).
	// Fica null até resolver: como o NavBar remonta a cada troca de página, sem isso o item
	// pisca com o rótulo errado por um instante a cada navegação. O hook mantém um cache em
	// memória compartilhado com o Dashboard, então o fetch roda só uma vez por sessão.
	const {
		isProfissional,
		modo,
		perfilEfetivo,
		podeAlternar,
		pendenciasOutroModo,
		alternar: alternarModo,
	} = useModoConta(userId);
	const trabalhando = perfilEfetivo === "profissional";

	// A troca de modo leva ao painel: é onde o modo novo se mostra inteiro. Ficar na página atual
	// (a busca, por exemplo) deixaria a pessoa em "trabalhando" olhando uma tela de quem contrata.
	const outroModo = modo === MODO.TRABALHANDO ? MODO.CONTRATANDO : MODO.TRABALHANDO;
	const rotuloTrocaDeModo = outroModo === MODO.CONTRATANDO ? "Mudar para Modo Cliente" : "Mudar para Modo Profissional";

	// A foto de perfil, do mesmo cache do hook acima — não custa requisição. Muda na hora quando o
	// Meu cadastro grava (definirAvatarUsuario), sem F5.
	const avatar = useAvatarUsuario(userId);

	// Enquanto o papel não é conhecido (token presente mas fetch/cache ainda não resolveram),
	// o item de destaque fica oculto em vez de mostrar um rótulo que pode estar errado.
	const menuDestaqueResolvido = !token || perfilEfetivo !== null;
	const menuDestaque = (!token || trabalhando)
		? { ...formularioContato, icone: "support_agent" }
		: { to: "/profissional", label: "Buscar profissional", icone: "search" };

	// No painel do sanduíche o destaque só existe para quem contrata ("Buscar profissional"). O
	// "Fale conosco" já tem lugar próprio ali — bloco no menu em blocos, último item da gaveta "Ajuda"
	// para o visitante —, e repeti-lo como destaque seria o mesmo link duas vezes no mesmo painel. O
	// header de desktop não tem nenhum dos dois, então lá `menuDestaque` vale para os dois rótulos.
	const mostrarDestaqueNoSanduiche = menuDestaqueResolvido && !!token && !trabalhando;

	// "Início" leva a "/", e no app "/" não é vitrine: resolve para o painel quando há sessão e
	// para o login quando não há (ver RaizDoApp em App.jsx). Com sessão, isso faz dele um segundo
	// caminho para o mesmo lugar que "Meu painel" -- que fica no item logo abaixo -- e o "você está
	// aqui" não acende em nenhum dos dois, porque "/" nunca chega a ser a rota atual.
	//
	// Sem sessão ele fica: ali "/" leva ao login, e é justamente a volta que a pessoa precisa ao
	// chegar nos Termos ou na Política a partir da tela de entrada.
	//
	// Na web nada disso vale -- "/" é a Home, e ela é um destino de verdade em qualquer estado.
	const mostrarInicio = !(ehNativo() && token);

	// Com sessão o sanduíche é a grade de blocos (components/MenuBlocos.jsx), na web e no app; sem
	// sessão continua a lista, porque Entrar/Criar conta são ações e não destinos de grade. Raciocínio
	// em prompts/menu-web-mobile-em-blocos.md.
	const menuEmBlocos = !!token;

	useEffect(() => {
		if (!userId) return;
		const fetchUnreadCount = async () => {
			try {
				const response = await apiClient.get(`/notificacao/checknaolida/${userId}`);
				const count = typeof response.data === 'number' ? response.data : parseInt(response.data, 10);
				setUnreadCount(isNaN(count) ? 0 : count);
			} catch {
				// silently ignore
			}
		};
		fetchUnreadCount();

		// Push com o app aberto: o Android não o mostra na bandeja, então o sino é o aviso — e ele só
		// é lido na montagem. Sem isto, a contagem ficaria parada até a próxima troca de tela.
		window.addEventListener(EVENTO_PUSH_RECEBIDO, fetchUnreadCount);
		return () => window.removeEventListener(EVENTO_PUSH_RECEBIDO, fetchUnreadCount);
	}, [userId]);

	const handleBellClick = async (e) => {
		e.preventDefault();
		setShowAccountMenu(false);
		// Contraparte do toggleMenu: sino e hambúrguer marcam o botão apertado com
		// o mesmo destaque, então os dois abertos ao mesmo tempo apareceriam como
		// duas seleções no header — sem contar o painel do menu por baixo.
		// (O menu da conta só existe no desktop, mas fechá-lo aqui é o que mantém
		// os dois em ordem quando a janela muda de largura com um deles aberto.)
		setIsMenuOpen(false);
		// Se já está aberta, apenas fecha
		if (showNotifications) {
			setShowNotifications(false);
			return;
		}
		// Abre imediatamente para que o spinner de carregamento fique visível
		setShowNotifications(true);
		if (userId) {
			setLoadingNotifications(true);
			setNotifError(null);
			try {
				const response = await apiClient.get(`/notificacao/listar/${userId}`);
				setNotifications(Array.isArray(response.data) ? response.data : [response.data]);
				setUnreadCount(0);
			} catch (err) {
				setNotifError(descreverErro('NTF-101', err));
			} finally {
				setLoadingNotifications(false);
			}
		}
	};

	const handleAccountClick = () => {
		setShowNotifications(false);
		setShowAccountMenu((prev) => !prev);
	};

	// O "sim" do diálogo. Os dois botões "Sair" só abrem a pergunta — quem encerra a sessão
	// é isto, que também tira da tela tudo o que estava aberto por trás do diálogo.
	//
	// A navegação NÃO espera a chamada ao servidor. `encerrarSessao` revoga o refresh token no
	// backend, e isso pode demorar ou falhar; segurar a tela até ela responder faria um botão que
	// promete "sair" ficar parado, e sair é justamente o que a pessoa acabou de pedir.
	//
	// O que torna isso correto é a ORDEM lá dentro: a sessão local é apagada antes da requisição,
	// de forma síncrona. O `navigate` abaixo acontece no mesmo tique, e a raiz decide lendo
	// `temSessao()` (ver RaizDoApp em App.jsx) — com a limpeza no fim, como já esteve, este clique
	// caía no painel de uma conta que acabara de sair.
	const handleLogout = () => {
		setConfirmarSaida(false);
		setShowAccountMenu(false);
		setIsMenuOpen(false);
		encerrarSessao();
		// `replace`: sair da conta é voltar ao começo, não um passo a desfazer — a tela de onde a
		// pessoa saiu não pode continuar no histórico como destino do botão voltar, porque voltar
		// para ela sem sessão só produziria um desvio para o login. No app vale duplamente: "/" é
		// decisão e não tela (ver RaizDoApp em App.jsx), e sem `replace` o voltar gastaria um toque
		// numa entrada que já foi trocada.
		navigate("/", { replace: true });
	};

	const toggleMenu = () => {
		setIsMenuOpen((prev) => {
			if (!prev) {
				// Evita o painel do menu coexistir com o de notificações — o outro
				// botão que também aparece no header em mobile.
				setShowNotifications(false);
			}
			return !prev;
		});
	};

	const closeMenu = () => setIsMenuOpen(false);

	// Painel e Minha agenda seguem o modo e se refazem no lugar; das outras telas a troca leva ao painel.
	const trocarDeModo = () => {
		alternarModo(outroModo);
		setIsMenuOpen(false);
		setShowAccountMenu(false);
		if (pathname !== "/agenda" && pathname !== "/dashboard") navigate("/dashboard");
	};

	// O ponto âmbar de "o outro modo tem coisa esperando você", com o texto para leitor de tela.
	const avisoOutroModo = pendenciasOutroModo > 0 ? (
		<>
			<span aria-hidden="true" className="ml-auto h-2.5 w-2.5 flex-none rounded-full bg-amber-500" />
			<span className="sr-only">
				{` (${pendenciasOutroModo} ${pendenciasOutroModo === 1 ? "pendência" : "pendências"})`}
			</span>
		</>
	) : null;

	// Logo do header: em desktop sempre leva pra "/" (vitrine). Em mobile, pra quem já
	// está logado, vira o atalho de volta ao painel — na web não há avatar nem "Meu painel" à
	// vista fora do menu sanduíche, e o logo é o único alvo sempre no ar. No app a aba
	// "Painel" já faz isso, mas o logo continua levando ao mesmo lugar: é o que qualquer
	// app faz com a marca no topo. matchMedia usa o mesmo 768px do breakpoint `md` do
	// Tailwind (ver tailwind.config.js, sem "screens" customizado); href="/" fica como
	// destino real do link, o preventDefault só troca o caminho nesse caso específico.
	const handleLogoClick = (e) => {
		if (token && window.matchMedia("(max-width: 767px)").matches) {
			e.preventDefault();
			navigate("/dashboard");
		}
	};

	// Links desktop como tabs de altura total: items-center centraliza o texto
	// na barra e pt-0.5 compensa os 2px do border-b-2
	const getDesktopNavClass = ({ isActive }) =>
		`inline-flex items-center border-b-2 px-1 pt-0.5 text-nav font-medium transition-colors duration-200 ${
			isActive
				? "border-primary text-primary"
				: "border-transparent text-dark hover:text-primary hover:border-primary/30"
		}`;

	// CTA de destaque ("Criar conta"): botão outline, não um tab de navegação. Sem o display
	// aqui — quem chama decide (é `hidden md:inline-flex`, porque no mobile ele vive no sanduíche).
	// whitespace-nowrap evita a quebra em duas linhas que deixava o botão mais alto que o Entrar
	const getDesktopCtaClass = ({ isActive }) =>
		`items-center whitespace-nowrap rounded-full border border-primary px-4 py-2 text-sm font-medium transition-colors duration-200 ${
			isActive
				? "bg-primary text-white"
				: "text-primary hover:bg-primary hover:text-white"
		}`;

	const getMobileNavClass = ({ isActive }) =>
		`block rounded-xl border-l-4 px-3 py-3.5 text-base font-medium transition-colors duration-200 ${
			isActive
				? "border-primary bg-primary/5 text-primary"
				: "border-transparent text-dark hover:bg-background hover:text-primary"
		}`;

	// Itens do menu de conta — só o dropdown do desktop os usa. No mobile o avatar não
	// existe mais: painel, cadastro e sair vivem nos blocos do menu sanduíche. Por isso as
	// medidas aqui são as do desktop, sem o par mobile/desktop que existia quando o bloco
	// era compartilhado.
	//
	// "Meu cadastro" não entra: no desktop ele já é um tab da barra horizontal, e
	// repeti-lo aqui daria dois caminhos visíveis ao mesmo tempo pro mesmo destino.
	const accountMenuItens = (
		<>
			<Link
				to="/dashboard"
				onClick={() => setShowAccountMenu(false)}
				className="block px-4 py-4 text-nav text-dark hover:bg-background hover:text-primary transition"
			>
				<span className="flex items-center gap-2">
					<Icon name="monitoring" className="h-7 w-7 flex-shrink-0" />
					Meu painel
				</span>
			</Link>
			{podeAlternar && (
				<button
					type="button"
					onClick={trocarDeModo}
					className="w-full text-left px-4 py-4 text-nav text-dark hover:bg-background hover:text-primary transition"
				>
					<span className="flex items-center gap-2">
						<Icon name="swap_horiz" className="h-7 w-7 flex-shrink-0" />
						{rotuloTrocaDeModo}
						{avisoOutroModo}
					</span>
				</button>
			)}
			{/* Só a partir do `lg`: o menu do avatar já aparece no `md`, mas abaixo de 1024px o
			    administrativo só mostra o aviso de "abra no computador" (AdminRoute, em App.jsx). */}
			{isAdmin && (
				<div className="hidden lg:block">
					<div className="my-1 border-t border-border" />
					<Link
						to="/admin/painel"
						onClick={() => setShowAccountMenu(false)}
						className="block px-4 py-4 text-nav text-dark hover:bg-background hover:text-primary transition"
					>
						<span className="flex items-center gap-2">
							<Icon name="build" className="h-7 w-7 flex-shrink-0" />
							Administrativo
						</span>
					</Link>
					<div className="my-1 border-t border-border" />
				</div>
			)}
			{/* O dropdown fica aberto por trás do diálogo: assim "Cancelar" devolve a pessoa
			    ao menu de onde ela veio, e o OverlaySheet consegue restaurar o foco neste
			    botão, que continua no DOM. */}
			<button
				onClick={() => setConfirmarSaida(true)}
				className="w-full text-left px-4 py-4 text-nav text-dark hover:bg-background hover:text-error transition"
			>
				<span className="flex items-center gap-2">
					<Icon name="logout" className="h-7 w-7 flex-shrink-0" />
					Sair
				</span>
			</button>
		</>
	);

	// Conteúdo da janela de notificações, reutilizado no dropdown (desktop) e no painel (mobile)
	const notificacoesConteudo = loadingNotifications ? (
		<div className="flex items-center justify-center py-12">
			<div
				role="status"
				aria-label="Carregando notificações"
				className="animate-spin rounded-full h-10 w-10 border-4 border-primary border-t-transparent"
			/>
		</div>
	) : notifError ? (
		<MensagemErro descricao={notifError} className="px-4 py-6" />
	) : notifications.length === 0 ? (
		<div className="text-muted text-sm px-4 py-6">Nenhuma notificação encontrada.</div>
	) : (
		/* Cards brancos sobre o bg-background do painel, no lugar das linhas divisórias. O
		   espaço entre eles é o p-1 do wrapper, e não space-y no <ul>: a margem ficaria fora do
		   <li> que a animação de saída achata e sobraria um degrau depois do colapso. O p-1
		   também dá folga para a sombra, que o overflow:hidden de .notificacao-item > * cortaria. */
		<ul className="px-2 py-2 md:px-5 md:py-3">
			{notifications.map((notificacao) => {
				const rota = rotaDaNotificacao(notificacao);
				// `data` desde o DTO de notificação (F4); `createdAt` é o nome antigo, repetido pela API.
				const quando = notificacao.data ?? notificacao.createdAt;
				const conteudo = (
					<>
						<div className="text-xs text-muted mb-1.5">{quando ? (() => { const d = new Date(quando); return d.getFullYear() < new Date().getFullYear() ? d.toLocaleDateString('pt-BR') : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }); })() : ''}</div>
						<div className="text-dark text-sm mb-1">{notificacao.texto}</div>
					</>
				);
				return (
				<li
					key={notificacao.id}
					className={`notificacao-item group${removingId === notificacao.id ? " notificacao-item--saindo" : ""}`}
				>
					<div className="p-1">
					<div className="bg-surface rounded-xl shadow-sm pl-4 pr-2 py-3 flex items-center justify-between">
						{/* Com destino (services/destinoDaNotificacao.js), o texto inteiro é o alvo do toque:
						    fecha o painel e leva ao assunto. Botão, e não Link: o painel precisa fechar
						    também quando a rota é a mesma da tela atual (o sheet da Agenda reabre pelo
						    parâmetro). Sem destino — avisos antigos, boas-vindas — fica só texto, como antes;
						    o "chevron" é o que diz, sem ler, quais levam a algum lugar. */}
						{rota ? (
							<button
								type="button"
								onClick={() => {
									setShowNotifications(false);
									navigate(rota);
								}}
								className="flex min-w-0 flex-1 items-center gap-2 rounded-lg text-left hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
							>
								<div className="min-w-0 flex-1">{conteudo}</div>
								<Icon name="chevron_right" className="h-5 w-5 shrink-0 text-muted" />
							</button>
						) : (
							<div>{conteudo}</div>
						)}
						{/* O p-1 que estava aqui sobrescrevia o p-2 do .btn-icone e deixava o alvo em
						    24px — num botão destrutivo, dentro do painel que no mobile ocupa a tela
						    inteira. p-2.5 leva a 20+20 = 40px sem esticar a linha, que tem duas
						    linhas de texto ao lado.

						    aria-label porque o title não é nome acessível confiável e, no toque,
						    não aparece nunca: sem ele o botão só se anuncia como "botão". */}
						<button
							className="btn-icone ml-3 p-2.5 hover:text-error disabled:hover:text-muted"
							aria-label="Excluir notificação"
							title="Excluir notificação"
							onClick={() => handleDeleteNotification(notificacao.id)}
							disabled={loadingNotifications || deletingId === notificacao.id}
						>
							{deletingId === notificacao.id ? (
								<Icon name="progress_activity" className="h-5 w-5 animate-spin" label="Excluindo notificação" />
							) : (
								<Icon name="delete" className="h-5 w-5" />
							)}
						</button>
					</div>
					</div>
				</li>
				);
			})}
		</ul>
	);

	// pt-[var(--safe-top)] no <nav>, e não na barra de dentro: o padding empurra o conteúdo para
	// baixo do relógio e da bateria, mas quem sobe até a borda da tela é o fundo (bg-white/90 +
	// backdrop-blur) — sem isso a faixa do status ficaria transparente sobre a página rolando.
	// Em navegador comum o inset é 0 e o padding não existe.
	return (
		<nav ref={navRef} className={`sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-border pt-[var(--safe-top)] transition-shadow duration-200 ${isScrolled ? "shadow-sm" : ""}`}>
		<div className="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
			{/* No mobile a barra é ~10% mais baixa que no desktop (4,5rem = 72px contra 80px),
			    pra devolver altura útil de tela. No app, com sessão, encolhe mais: 3,5rem (56px,
			    a top app bar pequena do Material), porque a navegação desceu para a barra de abas
			    e o header ficou só com logo e sino. Quem posiciona painel a partir da base do
			    header usa top-[var(--altura-header)] (styles/index.css), que já resolve os três
			    valores e soma o inset do topo — mudar a altura aqui é mudar a variável lá. */}
			<div className={`relative flex items-center justify-between gap-2 md:h-20 ${modoApp ? "h-14" : "h-[4.5rem]"}`}>
				{/* O hambúrguer não existe no app: quem abre o sanduíche lá é a aba do avatar. */}
				{!modoApp && (
				<div className="flex flex-shrink-0 items-center md:hidden">
					{/* Botão que abre/fecha o menu — o ícone continua sendo o sanduíche nos
					    dois estados; aberto, o botão ganha o mesmo destaque (fundo + cor
					    primária) usado nos links de navegação ativos, em vez de virar um X.
					    O ponto âmbar é o aviso de pendência do cadastro: ele morava no avatar,
					    que agora só existe no desktop, e sem um sinal aqui fora a pendência só
					    apareceria pra quem já tivesse aberto o menu. É decorativo (aria-hidden):
					    o mesmo aviso reaparece em âmbar, com texto, no bloco Meu cadastro. */}
					<button
						ref={hamburgerBtnRef}
						onClick={toggleMenu}
						type="button"
						className={`btn-icone relative focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary ${
							isMenuOpen
								? "bg-primary/10 text-primary"
								: "text-muted hover:bg-background hover:text-primary"
						}`}
						aria-controls="mobile-menu"
						aria-expanded={isMenuOpen}
						aria-label={isMenuOpen ? "Fechar menu" : "Abrir menu"}
					>
						<span className="absolute -inset-0.5"></span>
						<Icon name="menu" className="h-8 w-8" />
						{/* top/right positivos, e não o -top-0.5 -right-0.5 que o avatar usava:
						    aqui o ponto fica sobre a caixa do glifo, dentro do botão, e não
						    pendurado na borda de um círculo. O anel branco o separa do ícone. */}
						{cadastroPendente && (
							<span aria-hidden="true" className="absolute right-1 top-1 h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-white" />
						)}
					</button>
				</div>
				)}
				{/* Na web mobile o logo fica centrado entre o hambúrguer e o sino. No app não há
				    hambúrguer, e o logo vai para a esquerda, como a marca fica em qualquer top app
				    bar — centrado, sobraria um vazio à esquerda dizendo que falta alguma coisa ali.
				    O pl-2 devolve o respiro que o botão do hambúrguer dava; o h-11 (44px) é o maior
				    que cabe nos 56px do header com 6px de folga em cima e embaixo — o h-10 de antes
				    lia pequeno demais como única marca da barra. */}
				<div className={`flex min-w-0 flex-1 items-center md:items-stretch md:justify-start ${modoApp ? "justify-start pl-2" : "justify-center"}`}>
					<div className="flex min-w-0 items-center md:flex-shrink-0">
						<a href="/" onClick={handleLogoClick} className="block min-w-0">
							<img
								className={`hidden w-auto max-[299px]:block md:h-16 ${modoApp ? "h-11" : "h-[3.75rem]"}`}
								src="/images/nova-faxina-icone.svg"
								alt="Nova Faxina"
							/>
							<img
								className={`w-auto max-w-full object-contain max-[299px]:hidden md:h-16 md:max-w-none ${modoApp ? "h-11" : "h-[3.75rem]"}`}
								src="/images/logo.svg"
								alt="Nova Faxina"
							/>
						</a>
					</div>
					<div className="hidden md:ml-20 md:flex md:items-stretch">
					<div className="flex items-stretch space-x-4 lg:space-x-6" id="navbar-default">
						{mostrarInicio && (
						<NavLink
						to="/"
						end
						className={getDesktopNavClass}
						>
						Início
						</NavLink>
						)}

						{/* O ícone de pendência existia só no menu mobile: quem usa computador não via
						    sinal nenhum na barra e dependia do dashboard para descobrir que faltava
						    alguma coisa. Mesmo âmbar e mesmo destino do item mobile. */}
						<NavLink
						to={destinoCadastro}
						className={getDesktopNavClass}
						>
						Meu cadastro
						{cadastroPendente && (
							<Icon
								name="error"
								className="ml-1.5 h-4 w-4 flex-shrink-0 text-amber-500"
							/>
						)}
						</NavLink>

						{/* Só com sessão: /agenda é PrivateRoute, e para o visitante o item seria um
						    "Minha agenda" que abre o login — sem agenda nenhuma por trás. */}
						{token && (
						<NavLink
						to="/agenda"
						className={getDesktopNavClass}
						>
						Minha agenda
						</NavLink>
						)}

						{menuDestaqueResolvido && (
						<span className="flex items-stretch border-l border-border pl-4 lg:pl-6">
						<NavLink
						to={menuDestaque.to}
						className={getDesktopNavClass}
						>
						{/* Children como função para alcançar o `isActive` do NavLink: é ele que
						    decide entre o contorno e a variante cheia do ícone. */}
						{({ isActive }) => (
							<>
								{menuDestaque.label !== "Fale conosco" && (
									<Icon name={menuDestaque.icone} filled={isActive} className="mr-1.5 h-4 w-4" />
								)}
								{menuDestaque.label}
							</>
						)}
						</NavLink>
						</span>
						)}
					</div>
					</div>
				</div>
				   <div className="flex flex-shrink-0 items-center pr-2 md:ml-10 md:pr-0">
					   {token ? (
						   <>
							   <div className="flex items-center gap-1">
								   {/* Ícone de sino/notificação — antes do avatar: avatar é a âncora de
								       identidade e fica na ponta (mesmo padrão de Instagram/Facebook/
								       X/YouTube: notificações ao lado, perfil sempre por último).
								       No mobile o avatar não existe no header e o sino fica sozinho à
								       direita: é o único caminho pras notificações, que não têm item no
								       sanduíche. No app o avatar existe, mas na barra de abas, embaixo —
								       o sino continua sendo a única ação do header. */}
								   {firstName && (
									   <div className="relative">
										   {/* Mesmo estado "pressionado" do hambúrguer (bg-primary/10): o painel
										       aberto tampa boa parte da tela e sem o destaque não dá pra saber qual
										       botão o abriu — nem qual fecha de novo */}
										   <button
											   /* focus-visible, não focus: o botão continua com foco depois do
											      clique, e com `focus:ring` o anel ficava desenhado em volta do
											      destaque enquanto o painel estivesse aberto. Assim o anel só
											      aparece pra quem chega nele pelo teclado. */
											   className={`btn-icone p-3 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary ${
												   showNotifications
													   ? "bg-primary/10 text-primary"
													   : "text-muted hover:bg-background hover:text-primary"
											   }`}
											   onClick={handleBellClick}
											   aria-expanded={showNotifications}
											   aria-label={unreadCount > 0 ? `Notificações, ${unreadCount > 99 ? '99+' : unreadCount} não lidas` : "Notificações"}
										   >
										   <span className="relative inline-flex">
											   {/* 26px no mobile — sem avatar do lado pra comparar, o w-6 padrão
											       ficava pequeno demais como único ícone da barra. 28px no
											       desktop, um passo menor que o avatar ao lado (28px contra
											       32px): o glifo é vazado e preenche a caixa toda, então na
											       mesma medida lia como maior que o círculo cheio do avatar */}
											   <Icon name="notifications" className="w-[26px] h-[26px] md:w-7 md:h-7" />
											   {unreadCount > 0 && (
												   <span aria-hidden="true" className="absolute -top-1 -right-1 md:-top-1.5 md:-right-1.5 flex items-center justify-center min-w-[18px] h-[18px] md:min-w-[20px] md:h-[20px] px-1 rounded-full bg-red-600 text-white text-2xs font-bold leading-none">
													   {unreadCount > 99 ? '99+' : unreadCount}
												   </span>
											   )}
										   </span>
										   </button>
										   {/* Gaveta de notificações — desktop, presa à direita na altura inteira da
										       janela (o dropdown de 70dvh cortava a lista cedo). Via portal pelo mesmo
										       motivo do painel mobile abaixo. Cobre também o header: o sino fica atrás
										       do fundo, e quem fecha é o X, o clique no fundo ou o Escape. */}
										   {showNotifications && createPortal(
										   	<div className="hidden md:block fixed inset-0 z-50">
										   		<div onClick={() => setShowNotifications(false)} aria-hidden="true" className="absolute inset-0 bg-dark/40" />
										   		<div
										   			role="dialog"
										   			aria-modal="true"
										   			aria-label="Notificações"
										   			className="absolute inset-y-0 right-0 w-[28rem] max-w-full flex flex-col bg-surface shadow-xl"
										   		>
										   			<div className="flex items-center justify-between px-6 py-5 border-b border-border bg-primary/5">
										   				<span className="text-lg font-semibold text-dark">Notificações</span>
										   				<button
										   					onClick={() => setShowNotifications(false)}
										   					aria-label="Fechar notificações"
										   					className="btn-icone -mr-2 hover:text-primary"
										   				>
										   					<Icon name="close" className="h-6 w-6" />
										   				</button>
										   			</div>
										   			<div className="flex-1 min-h-0 overflow-y-auto bg-background">{notificacoesConteudo}</div>
										   		</div>
										   	</div>,
										   	document.body
										   )}
										   {/* Painel de notificações — mobile em tela cheia (via portal, pois o backdrop-blur
										       do nav quebraria position:fixed em descendentes). No app termina acima da
										       barra de abas (a variável só existe com ela montada; fora disso vale 0), e
										       o inset de baixo passa a ser dela — --safe-bottom-painel vale 0 nesse caso. */}
										   {showNotifications && createPortal(
										   	<div className="md:hidden fixed inset-x-0 top-[var(--altura-header)] bottom-[var(--altura-barra-inferior,0px)] z-50 flex flex-col bg-surface pb-[var(--safe-bottom-painel)]">
										   		{/* Cabeçalho inteiro fecha ao clicar, não só o X — área de toque maior */}
										   		<div
										   			onClick={() => setShowNotifications(false)}
										   			className="flex items-center justify-between px-4 py-3 border-b border-border bg-primary/5 cursor-pointer"
										   		>
										   			<span className="font-semibold text-dark">Notificações</span>
										   			<button
										   				onClick={() => setShowNotifications(false)}
										   				aria-label="Fechar notificações"
										   				className="btn-icone hover:text-primary"
										   			>
										   				<Icon name="close" className="h-6 w-6" />
										   			</button>
										   		</div>
										   		<div className="flex-1 min-h-0 overflow-y-auto bg-background">{notificacoesConteudo}</div>
										   	</div>,
										   	document.body
										   )}
									   </div>
								   )}
								   {/* Menu da conta só no desktop: no mobile tudo que ele oferecia está no
								       menu sanduíche — Meu painel, Meu cadastro e Sair nos blocos. A
								       exceção é o Administrativo, que só existe
								       aqui, e só a partir do `lg`. Dois painéis para o mesmo
								       assunto obrigavam a pessoa a adivinhar em qual olhar. No desktop ele
								       fica: lá não há sanduíche, e este é o único caminho pro logout.
								       (No app o avatar volta a existir, mas como aba da barra inferior que
								       abre o próprio sanduíche — não é este dropdown.) */}
								   <div className="relative hidden md:block">
									   <button
										   onClick={handleAccountClick}
										   type="button"
										   className="flex items-center gap-2 rounded-xl px-3 py-2 text-nav font-medium text-dark hover:bg-background hover:text-primary focus:outline-none transition"
										   aria-haspopup="true"
										   aria-expanded={showAccountMenu}
									   >
										   {/* Foto de perfil, ou as iniciais — as mesmas que os outros veem desta
										       pessoa. Sem nome no token (caso raro), cai no ícone genérico pro botão
										       não ficar vazio. Não há aviso de pendência aqui: no desktop ele mora
										       no tab "Meu cadastro" da barra horizontal. */}
										   {userName ? (
											   <InitialsAvatar name={userName} avatar={avatar} className="h-8 w-8 rounded-full text-sm" />
										   ) : (
											   <Icon name="account_circle" className="h-8 w-8" />
										   )}
										   <span>{firstName}</span>
										   <Icon name="keyboard_arrow_down" className={`h-4 w-4 text-muted transition-transform duration-200 ${showAccountMenu ? "rotate-180" : ""}`} />
									   </button>
									   {/* Dropdown ancorado ao avatar — único painel da conta desde que o
									       bottom sheet mobile saiu junto com o avatar do header mobile */}
									   {showAccountMenu && (
									   	<div className="absolute right-0 top-full mt-2 w-52 bg-surface border-caixa border-border rounded-xl shadow-lg z-50 py-1">
									   		{accountMenuItens}
									   	</div>
									   )}
								   </div>
							   </div>
						   </>
					   ) : (
						   <div className="flex items-center gap-3">
							   {/* "Criar conta" ao lado do "Entrar", e não no fim da lista de links: são o
							       par de ações de conta do visitante, e ficam juntas na ponta direita —
							       onde, para quem está logado, moram o sino e o avatar. Só no desktop:
							       no mobile o par vive no menu sanduíche (ver o bloco !token no painel). */}
							   <NavLink
								   to="/usuario/novo"
								   className={({ isActive }) => `hidden md:inline-flex ${getDesktopCtaClass({ isActive })}`}
							   >
								   Criar conta
							   </NavLink>
							   <Link
								   to="/login"
								   className="btn-primary text-base"
							   >
								   Entrar
							   </Link>
						   </div>
					   )}
				</div>
			</div>
		</div>
		{/* Painel mobile via portal: o backdrop-blur do nav cria um containing block
		    para position:fixed, então o painel precisa viver no body pra cobrir a
		    tela toda a partir da base do header (h-[4.5rem] md:h-20 → --altura-header, que
		    resolve o breakpoint e soma o inset do topo; o painel é md:hidden, então na
		    prática só o ramo mobile da variável importa aqui) */}
		{createPortal(
			<div className="md:hidden" ref={mobileMenuPortalRef}>
				{/* Painel imersivo, ocupa a tela toda abaixo do header. Sem overlay
				    separado: como cobre tudo, não sobra página visível pra escurecer.
				    Fecha pelo próprio botão do header (hambúrguer vira X) ou por Escape. */}
				<div
					id="mobile-menu"
					ref={menuPanelRef}
					role="dialog"
					aria-modal="true"
					aria-label="Menu de navegação"
					tabIndex={-1}
					// O respiro de baixo (2rem) fica no container, e não no último bloco: qual é
				// o último varia — Ajuda para o visitante, Sair para quem está logado, e no app a
				// assinatura da marca, abaixo dos dois. Somado ao --safe-bottom-painel porque em app
				// nativo a barra de gestos come esse espaço. Com a barra de abas
				// montada, o painel termina em cima dela (bottom-[var(--altura-barra-inferior)])
				// e o inset já é dela — a variável vale 0 nesse caso (styles/index.css).
				// bg-background com sessão: os blocos do MenuBlocos são caixas brancas, e sobre bg-white só a
				// sombra os separaria do fundo.
				className={`fixed inset-x-0 top-[var(--altura-header)] bottom-[var(--altura-barra-inferior,0px)] z-40 flex flex-col overflow-y-auto ${menuEmBlocos ? "bg-background" : "bg-white"} pb-[calc(2rem+var(--safe-bottom-painel))] outline-none transition-all duration-300 ease-in-out ${
						isMenuOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-4 pointer-events-none"
					}`}
				>
					{/* Com sessão, na web mobile e no app, o conteúdo é a grade de blocos
					    (components/MenuBlocos.jsx): identidade, troca de modo, destinos, Ajuda em sheet
					    e Sair. Os itens e as regras saem daqui. A lista, a gaveta Ajuda e o
					    Entrar/Criar conta abaixo ficam para o visitante. A assinatura da marca, no fim,
					    vale para os dois. */}
					{menuEmBlocos && (
						<MenuBlocos
							userName={userName}
							firstName={firstName}
							avatar={avatar}
							rotuloPapel={isProfissional === null ? null : isProfissional && trabalhando ? "Profissional" : "Cliente"}
							destinoCadastro={destinoCadastro}
							documentoPendente={documentoPendente}
							assinaturaPendente={assinaturaPendente}
							trabalhando={trabalhando}
							mostrarInicio={mostrarInicio}
							mostrarBusca={mostrarDestaqueNoSanduiche}
							trocaDeModo={podeAlternar ? {
								modoAtual: modo === MODO.TRABALHANDO ? "Você está no Modo Profissional" : "Você está no Modo Cliente",
								rotulo: rotuloTrocaDeModo,
								aviso: avisoOutroModo,
								onClick: trocarDeModo,
							} : null}
							linksAjuda={linksAjuda}
							estaEmPaginaAjuda={estaEmPaginaAjuda}
							ajudaAberta={ajudaEmSheetAberta}
							onAjuda={setAjudaEmSheetAberta}
							onNavegar={closeMenu}
							onSair={() => setConfirmarSaida(true)}
						/>
					)}

					{/* A lista do visitante: só "Início". Painel e agenda são PrivateRoute e abririam o
					    login, e o resto do que ele precisa vem logo abaixo (Entrar/Criar conta, Ajuda).
					    Sem flex-1: o vão, quando sobra, fica depois do último item, onde a assinatura
					    da marca do app (mt-auto) o usa para ler como rodapé. */}
					{!menuEmBlocos && (
					<nav className="space-y-1 px-4 py-4" aria-label="Navegação principal">
						<NavLink
							to="/"
							end
							onClick={closeMenu}
							className={getMobileNavClass}
						>
							{/* Ícone cheio só na página em que a pessoa está, junto com o rosa e o fundo
							    do getMobileNavClass: três sinais para o mesmo "você está aqui". */}
							{({ isActive }) => (
								<span className="flex items-center gap-2">
									<Icon name="home" filled={isActive} className="h-5 w-5 flex-shrink-0" />
									Início
								</span>
							)}
						</NavLink>
					</nav>
					)}

					{/* Entrar/Criar conta logo depois da navegação, e não no fim do
					    painel: são as ações de conversão do visitante deslogado e não
					    podem ficar abaixo de sete links secundários. */}
					{!token && (
						<div className="border-t border-border px-4 py-4">
							<div className="space-y-2">
								<Link
									to="/login"
									onClick={closeMenu}
									className="btn-primary w-full justify-center text-base"
								>
									Entrar
								</Link>
								<Link
									to="/usuario/novo"
									onClick={closeMenu}
									className="btn w-full justify-center text-base font-medium text-dark hover:bg-background hover:text-primary transition"
								>
									Criar conta
								</Link>
							</div>
						</div>
					)}

					{/* Ajuda: uma gaveta no lugar de blocos sempre abertos. Eram sete alvos de
					    rodapé disputando a tela com a navegação logo acima e empurrando o que
					    importa pra fora da dobra. No mobile o rodapé só existe na Home, e no app
					    em tela nenhuma (ver App.jsx), então este segue sendo o caminho para Termos/
					    Privacidade/Cookies em qualquer outra tela — agora atrás de um toque, com nome que
					    diz o que há lá dentro. Os canais de atendimento (WhatsApp, e-mail) já
					    não ficam aqui: o contato passa pelo "Fale conosco", no fim da lista,
					    que leva ao formulário do Quem somos — onde os canais também estão.

					    Hierarquia deliberadamente menor que a da navegação: mesma geometria
					    de linha (alvo de 44px), mas em text-dark/70, pra "Ajuda" não ler como
					    um sexto item de navegação.

					    Só do visitante: com sessão a Ajuda é um bloco que abre um sheet (MenuBlocos).
					    A linha em cima repete a do bloco Entrar/Criar conta, logo antes, e fecha a
					    sequência com a mesma divisão. */}
					{!menuEmBlocos && (
					<div className="border-t border-border px-4 py-4">
						{/* Heading + button, como no acordeão do FAQ (pages/Faq/ItemPergunta.jsx):
						    o rótulo continua sendo um título na árvore do leitor de tela, e o
						    disclosure segue o padrão WAI-ARIA (aria-expanded + aria-controls). */}
						<h2>
							<button
								type="button"
								onClick={() => setAjudaAberta((prev) => !prev)}
								aria-expanded={ajudaAberta}
								aria-controls="menu-ajuda"
								className="flex w-full items-center gap-2 rounded-xl px-3 py-3.5 text-base font-medium text-dark/70 transition-colors duration-200 hover:bg-background hover:text-primary"
							>
								{/* Sem `filled` no estado aberto: `help` não tem variante preenchida
								    gerada, e o chevron já diz se a gaveta está aberta. */}
								<Icon name="help" className="h-5 w-5 flex-shrink-0" />
								Ajuda
								<Icon
									name="keyboard_arrow_down"
									className={`ml-auto h-5 w-5 flex-shrink-0 text-muted transition-transform duration-200 ${ajudaAberta ? "rotate-180" : ""}`}
								/>
							</button>
						</h2>

						{/* A altura anima por grid-template-rows (ver .menu-sanfona em
						    styles/index.css), então o conteúdo precisa viver num filho único —
						    a div interna com o trilho é ela. O ref é do efeito que marca a
						    gaveta como inert enquanto fechada. */}
						<div id="menu-ajuda" ref={ajudaConteudoRef} data-aberta={ajudaAberta} className="menu-sanfona">
							<div>
								{/* Trilho: o fio à esquerda mais o recuo dizem que os itens
								    pertencem a "Ajuda" enquanto ela desliza, em vez de virarem
								    uma lista solta no meio do painel. */}
								<div className="ml-4 border-l border-border pl-3 pt-1">
									{/* NavLink, e não Link: com a gaveta abrindo sozinha na página em
									    que a pessoa está, o item precisa dizer qual é. O ativo é só a
									    cor — sem fundo nem borda, que são a marca dos itens de
									    navegação de verdade, uma hierarquia acima daqui.

									    Cada link é um `flex` de largura total (um por linha, alvo de
									    44px), e não o `.btn` que a lista usava antes: aquele é
									    inline-flex com px-6, então os cinco rótulos fluíam como um
									    varal de pílulas de larguras diferentes, quebrando a linha
									    onde desse. Dentro do trilho a leitura tem que ser de lista. */}
									<nav aria-label="Ajuda e condições">
										{linksAjuda.map((link) => (
											<NavLink
												key={link.to}
												to={link.to}
												onClick={closeMenu}
												className={({ isActive }) =>
													`flex items-center rounded-xl px-3 py-3 text-sm transition-colors duration-200 hover:bg-background hover:text-primary ${
														isActive ? "font-medium text-primary" : "text-dark/70"
													}`
												}
											>
												{link.label}
											</NavLink>
										))}
										{/* "Fale conosco" fecha a lista, depois de "Quem somos", que é a página
										    onde o formulário mora. É o único lugar dele no painel do visitante;
										    com sessão ele é bloco no MenuBlocos.

										    Fora de `linksInstitucionais` porque o rodapé já o mostra como canal de
										    "Atendimento" (ver config). E `Link`, não `NavLink`: o ativo do NavLink
										    casa pelo pathname, então em /quemsomos ele acenderia junto com "Quem
										    somos" — a página é uma só, e um item aceso basta. */}
										<Link
											to={formularioContato.to}
											onClick={closeMenu}
											className="flex items-center rounded-xl px-3 py-3 text-sm text-dark/70 transition-colors duration-200 hover:bg-background hover:text-primary"
										>
											{formularioContato.label}
										</Link>
									</nav>
								</div>
							</div>
						</div>
					</div>
					)}

					{/* O item "Administrativo" saiu daqui: este menu só existe abaixo do `md`, e o
					    administrativo é só para o computador (abaixo do `lg` a rota mostra um aviso;
					    ver AdminRoute em App.jsx). O caminho para ele é o menu do avatar. O "Sair"
					    do celular mora no fim do MenuBlocos, o único painel que existe com sessão. */}

					{/* A assinatura da marca (tagline + versão) fecha o painel — só no app, onde
					    página nenhuma tem rodapé (ver App.jsx) e este é o único lugar em que a versão
					    existe fora da tela de login. Na web ela já fecha a própria página, e repeti-la
					    aqui seria a mesma linha duas vezes na mesma tela.

					    Abaixo do Sair para quem está logado e abaixo de Ajuda para o visitante: não
					    depende de `token`, porque quem lê os Termos a partir do login também precisa
					    achar a versão. Sem border-t, pelo motivo que o componente explica; o mt-auto
					    é o que a leva ao pé da tela quando a lista é curta (ver a nota do <nav>). */}
					{ehNativo() && <AssinaturaMarca className="mt-auto px-4 pt-6" />}
				</div>
			</div>,
			document.body
		)}

		{/* A barra de abas do app. Recebe o mesmo `toggleMenu` do hambúrguer e o mesmo ref de
		    devolução de foco: para o painel do sanduíche, a aba do avatar É o botão que o abre. */}
		{comAbas && (
			<BarraInferior
				ref={barraInferiorRef}
				botaoAvatarRef={hamburgerBtnRef}
				perfilEfetivo={perfilEfetivo}
				avisoOutroModo={pendenciasOutroModo > 0}
				firstName={firstName}
				userName={userName}
				avatar={avatar}
				isMenuOpen={isMenuOpen}
				onToggleMenu={toggleMenu}
				cadastroPendente={cadastroPendente}
			/>
		)}

		{/* Confirmação de saída — uma instância só para os dois "Sair" (menu sanduíche e
		    dropdown do avatar): o OverlaySheet por baixo se porta pro body, então quem a
		    renderiza não decide onde ela aparece. No mobile sobe como bottom sheet, no
		    desktop é cartão centrado, e ele cuida de Escape, clique fora, trava de rolagem
		    e devolução do foco ao botão de origem.

		    Diz o que acontece, em vez de perguntar "tem certeza?" — mesma regra de texto
		    do DialogoAbrirMapa. A segunda frase é o que responde à hesitação real de quem
		    hesita em sair: se perde alguma coisa fazendo isso. */}
		<DialogoConfirmacao
			aberto={confirmarSaida}
			titulo="Sair da conta"
			rotuloConfirmar="Sim, encerrar sessão"
			onFechar={() => setConfirmarSaida(false)}
			onConfirmar={handleLogout}
		>
			<p className="t-corpo p-4">
				Você vai encerrar a sessão neste aparelho. Seus agendamentos e seu cadastro	continuam salvos. Continuar?
			</p>
		</DialogoConfirmacao>
		</nav>
	);
}
